import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import useAuth from "../../../hooks/useAuth";
import useRetryFetch from "../../../hooks/useRetryFetch";
import { listPwfs, updatePwf } from "../../../api/pwf";
import { queryClient } from "../../../lib/queryClient";
import { readCockpitState, type CockpitState } from "../../../lib/cockpitState";

export { readCockpitState } from "../../../lib/cockpitState";
export type { CockpitState } from "../../../lib/cockpitState";

export function useCockpitState(): CockpitState & { isLoading: boolean } {
  const { auth } = useAuth();
  const retryFetch = useRetryFetch();
  const enabled = Boolean(auth?.email && auth?.accessToken);
  const { data, isLoading } = useQuery({
    queryKey: ["cockpit-projects"],
    queryFn: () => listPwfs(retryFetch),
    enabled,
  });
  return { ...readCockpitState(data ?? []), isLoading: enabled && isLoading };
}

/** Patch config.cockpit on the compass node, preserving all sibling keys. */
export function usePatchCockpitConfig() {
  const retryFetch = useRetryFetch();
  return useCallback(
    async (patch: Record<string, unknown>) => {
      const pwfs = await listPwfs(retryFetch);
      const { compassId } = readCockpitState(pwfs ?? []);
      const compass = (pwfs ?? []).find((p) => p._id === compassId) ?? null;
      if (!compass) return;
      await updatePwf(retryFetch, compass._id, {
        config: {
          ...(compass.config as any),
          cockpit: { ...((compass.config as any)?.cockpit ?? {}), ...patch },
        },
      });
      queryClient.invalidateQueries({ queryKey: ["cockpit-projects"] });
    },
    [retryFetch]
  );
}
